import detectIncognito from 'detectincognitojs'
import { omitBy } from 'lodash'
import mixpanel from 'mixpanel-browser'
import { GetStaticProps, NextPage } from 'next'
import dynamic from 'next/dynamic'
// import Link from 'next/link'
import { useRouter } from 'next/router'
import React, { Dispatch, Fragment, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AnnouncementNotification } from 'src/components/AnnouncementNotification/AnnouncementNotification'
import { getFingerprint } from 'src/components/CustomHooks/useFingerprint'
// import { ConsultantDetailContainer } from 'src/components/ConsultantDetailContainer/ConsultantDetailContainer'
// import { ConsultantEnhancedDetailContainer } from 'src/components/ConsultantEnhancedDetailContainer/ConsultantEnhancedDetailContainer'
import { EnhancedRequestContainer } from 'src/components/EditListing/EnhancedRequestContainer'
import { Footer } from 'src/components/Footer/Footer'
//import { Breadcrumb } from 'src/components/global/Breadcrumb'
import MainContainer from 'src/components/global/MainContainer/MainContainer'
import { ThemeOnly } from 'src/components/global/ThemeOnly'
import { IltaHeader } from 'src/components/IltaHeader/IltaHeader'
import FreeSearchPopup from 'src/components/Popup/FreeSearchPopup'
import SkeletonLoading from 'src/components/SkeletonLoading/SkeletonLoading'
///import { SolutionEnhancedHeader } from 'src/components/SolutionsPage/SolutionEnhancedHeader/SolutionEnhancedHeader'
//import { SolutionHeader } from 'src/components/SolutionsPage/SolutionHeader/SolutionHeader'
// import { Testimonials } from 'src/components/Testimonials/Testimonials'
import * as enVariables from 'src/config/config'
import { apolloSdk } from 'src/graphql/apolloSdk'
import {
  IConsultancySolutionDataFragment,
  ISimilarSolutionItemFragment,
  ITestimonialItemFragment,
  useCheckFreeSearchLazyQuery,
  useRelatedContentBySolutionLazyQuery,
  useShowRelatedContentFeatureLazyQuery,
  useSimilarSolutionsLazyQuery,
} from 'src/graphql/generated/hooks'
import { useAuthenticated } from 'src/hooks/useAuthenticated'
import { useDefaultLogo } from 'src/hooks/useDefaultLogo'
import { Meta } from 'src/layout/Meta'
import { showToast } from 'src/store/actions/toastAction'
import { Main } from 'src/templates/Main'
import { FilterOption, REVIEWS_TAB, SimpleSubTopicType } from 'src/types'
import { getAuthorsFilter, getDateFilter } from 'src/utils/ContentFilters/contentFilters'
import { withRetries } from 'src/utils/retryHelper'

import { SolutionBreadcrumb } from '../../components/SolutionBreadcrumb/SolutionBreadcrumb'
import { AppState } from '../../store/types'

type Props = {
  solution: IConsultancySolutionDataFragment
  testimonials: ITestimonialItemFragment[]
  showVendorAnalysis: boolean
  analysisTotal: number
  productBriefingTotal: number
  showProductBriefing: boolean
  vendorUsers: string[]
  _testimonials: ITestimonialItemFragment[]
}

const Breadcrumb = dynamic(() => import('src/components/global/Breadcrumb').then((mod) => mod.Breadcrumb), {
  ssr: false,
  loading: () => <SkeletonLoading className="bg-navy" />,
})
const SolutionEnhancedHeader = dynamic(
  () =>
    import('src/components/SolutionsPage/SolutionEnhancedHeader/SolutionEnhancedHeader').then(
      (mod) => mod.SolutionEnhancedHeader,
    ),
  {
    ssr: false,
    loading: () => <SkeletonLoading className="bg-navy" />,
  },
)
const SolutionHeader = dynamic(
  () => import('src/components/SolutionsPage/SolutionHeader/SolutionHeader').then((mod) => mod.SolutionHeader),
  {
    ssr: false,
    loading: () => <SkeletonLoading className="bg-navy" />,
  },
)
const ConsultantEnhancedDetailContainer = dynamic(
  () =>
    import('src/components/ConsultantEnhancedDetailContainer/ConsultantEnhancedDetailContainer').then(
      (mod) => mod.ConsultantEnhancedDetailContainer,
    ),
  {
    ssr: false,
    loading: () => <SkeletonLoading className="bg-navy" />,
  },
)
const ConsultantDetailContainer = dynamic(
  () =>
    import('src/components/ConsultantDetailContainer/ConsultantDetailContainer').then(
      (mod) => mod.ConsultantDetailContainer,
    ),
  {
    ssr: false,
    loading: () => <SkeletonLoading className="bg-navy" />,
  },
)
const Testimonials = dynamic(() => import('src/components/Testimonials/Testimonials').then((mod) => mod.Testimonials), {
  ssr: false,
  loading: () => <SkeletonLoading className="bg-navy" />,
})

const ConsultantPage: NextPage<Props> = ({
  solution,
  testimonials,
  showVendorAnalysis,
  analysisTotal,
  productBriefingTotal,
  showProductBriefing,
  vendorUsers,
  _testimonials,
}: Props) => {
  const originInfo = useSelector((state: AppState) => state.originInfo)
  const prevPath = originInfo.lastOrigin || ''
  const router = useRouter()
  const [topicInfo, setTopicInfo] = useState<SimpleSubTopicType>()
  const [subTopicInfo, setSubTopicInfo] = useState<SimpleSubTopicType>()
  const [isFreeSearch, setIsFreeSearch] = useState<boolean | null>(null)
  const [checkFreeSearch, { data: checkFreeSearchData }] = useCheckFreeSearchLazyQuery()
  const [isNonLogin, setIsNonLogin] = useState(false)
  const dispatch: Dispatch<any> = useDispatch()

  const [tabIdx, setTabIdx] = useState(0)
  const onSelectTab = (index: number) => setTabIdx(index)
  const imageUrl = useDefaultLogo(solution?.logo?.url)
  const displayName = solution?.displayName || ''
  const shortDescription = solution?.shortDescription || ''
  const slug = solution?.slug || ''
  const isEnhanced = solution?.isEnhanced
  const isPremium = solution?.isPremium
  // const { announcement, optionalLink, publishedAt } = solution?.announcement ?? {}

  const emptyAnnouncement = {
    announcement: '',
    optionalLink: '',
    publishedAt: '',
    isArchived: false,
  }
  const activeAnnouncements = solution?.announcement?.filter((item) => item != null && !item.isArchived) ?? []
  const announcementData = activeAnnouncements[0] ?? emptyAnnouncement
  const { announcement = '', optionalLink = '', publishedAt = '' } = announcementData

  const { session } = useAuthenticated()
  const randomNum = solution?.documentId ?? solution?.slug ?? Math.floor(Math.random() * 1000) // Generates a number between 0 and 999
  const [loadSimilarVendors, { data: similarVendorsData }] = useSimilarSolutionsLazyQuery()
  const [loadShowRelatedContentFeature, { data: permissionData }] = useShowRelatedContentFeatureLazyQuery()
  const [checkFeaturePermission, setCheckFeaturePermission] = useState(false)
  const similars = useMemo<ISimilarSolutionItemFragment[]>(() => {
    return (
      similarVendorsData?.similarSolutions.solutions.filter((item): item is ISimilarSolutionItemFragment => !!item) ||
      []
    )
  }, [similarVendorsData])
  const [relatedContent, { data: relatedContentData }] = useRelatedContentBySolutionLazyQuery()
  const [solutionRelatedContent, setSolutionRelatedContent] = useState<any[]>([])
  const [totalRelatedContent, setTotalRelatedContent] = useState(0)

  const fetchData = async () => {
    const from = router.query.from as string
    const selectedTopic = {
      slug: 'consultants',
      name: 'Consultants',
    }
    setTopicInfo(selectedTopic)

    solution?.subTopics?.map((subTopic) => {
      if (subTopic?.slug === from) {
        const selected: SimpleSubTopicType = {
          slug: subTopic.slug || '',
          name: subTopic.name,
        }
        setSubTopicInfo(selected)
      }
    })

    if (session?.user?.idToken && session?.user?.idToken !== '') {
      loadSimilarVendors({
        variables: {
          id: solution.documentId || '',
          from: from,
        },
        context: {
          headers: {
            Authorization: `Bearer ${session?.user?.idToken}`,
          },
        },
      })
    } else {
      loadSimilarVendors({
        variables: {
          id: solution.documentId || '',
          from: from,
        },
      })
    }

    const query = {
      offset: 0,
      limit: 10,
      sort: 'Category_Chronologically',
      slug: slug,
      relatedContent: 1,
    }
    if (enVariables.SHOW_RELATED_CONTENT) {
      relatedContent({ variables: { query: omitBy(query, (value) => !value) } })
    }
  }

  useEffect(() => {
    const fetchData = async () => {
      if (typeof window !== 'undefined') {
        const lastAuthUser =
          localStorage.getItem(
            `CognitoIdentityServiceProvider.${enVariables.AMPLIFY_USER_POOL_APP_CLIENT_ID}.LastAuthUser`,
          ) || localStorage.getItem(`sso`)
        setIsNonLogin(!lastAuthUser)

        try {
          const browser = await detectIncognito()
          const fingerprintID = await getFingerprint()
          const customAccessQuery = solution?.documentId

          if (enVariables.SHOW_FREE_SEARCH && fingerprintID && customAccessQuery) {
            const query = {
              accessFreeSearch: true,
              uniqueAddress: !browser?.isPrivate && browser?.browserName !== '' ? fingerprintID : '',
              customAccessQuery: customAccessQuery,
            }

            await checkFreeSearch({ variables: query, fetchPolicy: 'no-cache' })
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error in checking free search is available:')
        }
      }
    }
    if (slug) {
      fetchData()
    }
  }, [slug])

  useEffect(() => {
    if (checkFreeSearchData) {
      // console.log(checkFreeSearchData)

      const isFreeSearch = checkFreeSearchData?.checkFreeSearch?.isFreeSearch
      if (isFreeSearch != null) {
        setIsFreeSearch(isFreeSearch)
      }

      if (
        isNonLogin &&
        checkFreeSearchData &&
        checkFreeSearchData?.checkFreeSearch?.isFreeSearch == true &&
        checkFreeSearchData?.checkFreeSearch?.isFreeSearchLeft == 1
      ) {
        dispatch(showToast('This is your last free search for the week. ', 'non_r_last_free_search'))
      }
      if (
        !isNonLogin &&
        checkFreeSearchData &&
        checkFreeSearchData?.checkFreeSearch?.isFreeSearch == true &&
        checkFreeSearchData?.checkFreeSearch?.isFreeSearchLeft == 1
      ) {
        dispatch(showToast('This is your last free search for the week. ', 'r_last_free_search'))
      }
    }
  }, [checkFreeSearchData])

  useEffect(() => {
    if (relatedContentData?.relatedContentBySolution?.data?.contents) {
      setSolutionRelatedContent(relatedContentData?.relatedContentBySolution?.data?.contents || [])
      setTotalRelatedContent(relatedContentData?.relatedContentBySolution?.data?.total || 0)
    }
  }, [relatedContentData])

  useEffect(() => {
    if (enVariables.ENVIRONMENT === 'production') {
      mixpanel.track('visit_product', {
        model: 'Consultant',
        displayName,
        id: solution?.documentId,
        subtopic: solution?.subTopics?.map((item) => item?.name),
        isEnhanced,
        isPremium,
      })
    }
  }, [solution, displayName, isEnhanced, isPremium])

  useEffect(() => {
    fetchData()
    if (router.query['from'] === 'submitReview') onSelectTab(REVIEWS_TAB)
  }, [router])

  useEffect(() => {
    const fetchData = async () => {
      try {
        await loadShowRelatedContentFeature()
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error in checking that user have Show Related Content Permission')
      }
    }
    fetchData()
  }, [])

  useEffect(() => {
    if (permissionData?.showRelatedContentFeature || enVariables.SHOW_RELATED_CONTENT_TO_ALL) {
      setCheckFeaturePermission(permissionData?.showRelatedContentFeature || enVariables.SHOW_RELATED_CONTENT_TO_ALL)
    }
  }, [permissionData])

  const meta = (
    <Meta title={displayName} description={shortDescription} url={`/consultants/${slug}/`} image={imageUrl} />
  )

  const freeSearchWithAllCTA = (
    <FreeSearchPopup
      heading={`You have reached your final search for the week.`}
      title={<>Register a free account on LTH to continue searching.</>}
      createAccountButton={true}
      premiumSignupButton={false}
      foundSolution={false}
    />
  )

  const freeSearchWithPremiumButton = (
    <FreeSearchPopup
      heading={`You have reached your final free search for the week.`}
      title={
        <>
          Sign up to LTH <span className="text-[#3329EF]">Premium</span> for unlimited searches.
        </>
      }
      createAccountButton={false}
      premiumSignupButton={true}
      foundSolution={false}
    />
  )

  return (
    <Main meta={meta}>
      <MainContainer id="mainContainer">
        <ThemeOnly theme="ilta">
          <IltaHeader />
        </ThemeOnly>
        <div className="flex flex-col bg-clouds">
          <div className="flex flex-col w-full items-center">
            {router.query['viewType'] === 'manage' && (
              <Breadcrumb
                subject={'MY ACCOUNT'}
                subjectRoute={'/my-account'}
                subtopic={'MY SOLUTIONS'}
                subtopicRoute={'/my-solutions'}
                subtitle={displayName}
              />
            )}

            {router.query['viewType'] !== 'manage' && (
              <Fragment>
                {prevPath ? (
                  <SolutionBreadcrumb />
                ) : (
                  <div className="!max-w-[1100px] flex flex-row w-full mt-[20px] mb-[10px] pl-[1.25rem]">
                    <Breadcrumb
                      subject={topicInfo?.name}
                      subjectRoute={`/topics/${topicInfo?.slug}`}
                      subtopic={subTopicInfo?.name}
                      subtopicRoute={`/topics/${topicInfo?.slug}/${subTopicInfo?.slug}`}
                      subtitle={displayName}
                    />
                  </div>
                )}
              </Fragment>
            )}
          </div>

          {(isEnhanced || isPremium) && !!solution?.consultantData ? (
            <SolutionEnhancedHeader
              key={`${randomNum}-SolutionEnhancedHeader`}
              solution={solution}
              onSelectTab={onSelectTab}
              tabIdx={tabIdx}
              showVendorAnalysis={showVendorAnalysis}
              showProductBriefing={showProductBriefing}
              vendorUsers={vendorUsers}
            />
          ) : (
            <SolutionHeader
              key={`${randomNum}-SolutionHeader`}
              solution={solution}
              tabIdx={tabIdx}
              onSelectTab={onSelectTab}
              showVendorAnalysis={showVendorAnalysis}
              showProductBriefing={showProductBriefing}
              vendorUsers={vendorUsers}
            />
          )}
        </div>
        <hr />
        {announcement && publishedAt && (
          <div className="flex justify-center">
            <div className="flex flex-col pl-[1.25rem] pr-[1.25rem] !max-w-[1100px] w-full">
              {optionalLink ? (
                <AnnouncementNotification
                  key={`${randomNum}-AnnouncementNotification-link`}
                  announcement={announcement}
                  publishedAt={publishedAt}
                  optionalLink={optionalLink}
                />
              ) : (
                <AnnouncementNotification
                  key={`${randomNum}-AnnouncementNotification`}
                  announcement={announcement}
                  publishedAt={publishedAt}
                />
              )}
            </div>
          </div>
        )}
        {!isEnhanced && router.query['viewType'] === 'manage' && vendorUsers.includes(session?.user.id || '') && (
          <EnhancedRequestContainer
            key={`${randomNum}-EnhancedRequestContainer`}
            solution_id={solution.documentId || ''}
          />
        )}
        <div className="flex flex-row justify-center w-full">
          <div
            className={`flex flex-row justify-between w-full  mb-32 ${!enVariables.SHOW_FREE_SEARCH || (enVariables.SHOW_FREE_SEARCH && (isFreeSearch == null || isFreeSearch)) ? '!max-w-[1100px]' : 'show-popup-search-rectangle'}`}
          >
            {isNonLogin && !isFreeSearch && isFreeSearch != null && freeSearchWithAllCTA}
            {!isNonLogin && !isFreeSearch && isFreeSearch != null && freeSearchWithPremiumButton}
            {(!enVariables.SHOW_FREE_SEARCH ||
              (enVariables.SHOW_FREE_SEARCH && (isFreeSearch == null || isFreeSearch))) &&
              ((isEnhanced || isPremium) && !!solution?.consultantData ? (
                <ConsultantEnhancedDetailContainer
                  key={`${randomNum}-ConsultantEnhancedDetailContainer`}
                  consultant={solution}
                  tabIdx={tabIdx}
                  similars={similars}
                  analysisTotal={analysisTotal}
                  productBriefingTotal={productBriefingTotal}
                  relatedContent={checkFeaturePermission ? solutionRelatedContent || [] : []}
                  totalRelatedContent={checkFeaturePermission ? totalRelatedContent : 0}
                />
              ) : (
                <ConsultantDetailContainer
                  key={`${randomNum}-ConsultantDetailContainer`}
                  consultant={solution}
                  tabIdx={tabIdx}
                  similars={similars}
                  analysisTotal={analysisTotal}
                  productBriefingTotal={productBriefingTotal}
                  relatedContent={checkFeaturePermission ? solutionRelatedContent : []}
                  totalRelatedContent={checkFeaturePermission ? totalRelatedContent : 0}
                />
              ))}
          </div>
        </div>
        {(!enVariables.SHOW_FREE_SEARCH || (enVariables.SHOW_FREE_SEARCH && (isFreeSearch == null || isFreeSearch))) &&
          (isEnhanced || isPremium) &&
          testimonials?.length > 0 && (
            <ThemeOnly theme="legaltech">
              <Testimonials key={`${randomNum}-testimonials`} testimonials={testimonials || []} />
            </ThemeOnly>
          )}
        {(!enVariables.SHOW_FREE_SEARCH || (enVariables.SHOW_FREE_SEARCH && (isFreeSearch == null || isFreeSearch))) &&
          !isEnhanced &&
          !isPremium && <Testimonials testimonials={_testimonials} />}
        <ThemeOnly theme="legaltech">
          <Footer />
        </ThemeOnly>
      </MainContainer>
    </Main>
  )
}

export default ConsultantPage

export const getStaticProps: GetStaticProps<Props> = async ({ params }) => {
  if (!params?.slug) return { revalidate: 60, notFound: true }
  try {
    const slug = params.slug as string
    const [{ solutions }, { testimonialsPlural }] = await Promise.all([
      withRetries(() => apolloSdk.consultancyBySlug({ slug, includeIlta: enVariables.THEME === 'ilta' })),
      withRetries(() => apolloSdk.globalTestimonials()),
    ])

    const solution = solutions && solutions.length > 0 ? solutions?.[0] : null
    if (!solution) return { revalidate: 60, notFound: true }

    const vendorUsers =
      (solution?.vendor?.users.map((user) => user?.external_id || null).filter((item) => item !== null) as string[]) ||
      []

    const testimonials: any[] = (solution?.consultantData?.enhancedListingData?.testimonials || []).map(
      (testimonial) => {
        return {
          ...testimonial,
          description: testimonial?.description || '',
          author: {
            name: testimonial?.author?.name || '',
            role: testimonial?.author?.role || '',
            company: testimonial?.author?.company || '',
            avatar: {
              url: testimonial?.author?.avatar?.url || '',
            },
          },
        }
      },
    )
    const { contentResults } = await withRetries(() =>
      apolloSdk.searchContents({
        query: { contentType: 'Vendor_Analysis', resourceSolutionIds: solution.documentId },
      }),
    )

    const { contentResults: productBriefingContentResults } = await withRetries(() =>
      apolloSdk.searchContents({
        query: { documentType: 'Product Briefing', resourceSolutionIds: solution.documentId || '' },
      }),
    )

    const filters: FilterOption[] = await Promise.all([
      getAuthorsFilter({ includeAuthors: true, includeExperts: true }),
      getDateFilter(),
    ])

    return {
      props: {
        solution,
        testimonials,
        filters,
        showVendorAnalysis: contentResults.data?.total !== 0,
        analysisTotal: contentResults?.data?.total || 0,
        productBriefingTotal: productBriefingContentResults?.data?.total || 0,
        showProductBriefing: productBriefingContentResults?.data?.total !== 0,
        vendorUsers,
        _testimonials: (testimonialsPlural || []).filter((item): item is ITestimonialItemFragment => item !== null),
      },
      revalidate: 60, // Only cache successful pages
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error fetching consultant:', error)
    return {
      notFound: true,
      revalidate: 5,
    }
  }
}

export async function getStaticPaths() {
  const { solutions } = await apolloSdk.allSolutionSlugs({ start: 0, type: 'Consultancy' })
  const result = (solutions && solutions.length > 0 ? solutions : []) || []

  const paths = result.filter((item) => !!item?.slug).map((item) => ({ params: { slug: item!.slug } }))
  return {
    paths,
    fallback: 'blocking',
  }
}
